import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './CheckoutPage.css';

const BASE_URL = 'https://agro.atrmart.in/api';

const CheckoutPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        cartItems    = [],
        subtotal     = 0,
        taxAmount    = 0,
        estimatedTotal = 0,
    } = location.state || {};

    const [step, setStep]       = useState(1);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '', phone: '', email: '',
        doorNo: '', street: '', area: '',
        city: 'Madurai', state: 'Tamil Nadu',
        instructions: '',
        paymentMethod: 'razorpay',
    });

    const handleInputChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const prevStep = () => setStep(s => s - 1);

    const nextStep = () => {
        if (step === 1 && (!formData.name || !formData.phone)) {
            alert("Please provide the receiver's name and phone number.");
            return;
        }
        if (step === 2 && (!formData.doorNo || !formData.street || !formData.area)) {
            alert('Please fill in your Door No, Street, and Area.');
            return;
        }
        setStep(s => s + 1);
    };

    // ── Open Razorpay payment modal ──────────────────────────────────────────
    const handlePlaceOrder = async () => {
        setLoading(true);
        try {
            const res  = await fetch(`${BASE_URL}/create-razorpay-order`, {
                method:  'POST',
                headers: {
                    'Content-Type':  'application/json',
                    'Accept':        'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('user_token')}`,
                },
                body: JSON.stringify({ amount: estimatedTotal }),
            });

            if (!res.ok) {
                const txt = await res.text();
                console.error('Server error:', txt);
                alert('Could not create payment. Check Laravel logs.');
                setLoading(false);
                return;
            }

            const data = await res.json();

            const options = {
                key:      data.key,
                amount:   data.amount,
                currency: 'INR',
                name:     'ATR AGRO MART',
                order_id: data.id,

                // ✅ This runs AFTER successful payment
                handler: (response) => {
                    submitOrderToBackend(
                        response.razorpay_payment_id,
                        response.razorpay_order_id,
                        response.razorpay_signature
                    );
                },

                modal: { ondismiss: () => setLoading(false) },

                prefill: {
                    name:    formData.name,
                    contact: formData.phone,
                    email:   formData.email || '',
                },
                theme: { color: '#1a5d1a' },
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', () => {
                setLoading(false);
                alert('Payment failed. Please try again.');
            });
            rzp.open();

        } catch (err) {
            console.error('Payment init failed:', err);
            alert('Something went wrong. Please check your connection.');
            setLoading(false);
        }
    };

    // ── After Razorpay confirms payment: verify + save order + notify seller ─
    const submitOrderToBackend = async (paymentId, razorpayOrderId, signature) => {
        const payload = {
            razorpay_payment_id: paymentId,
            razorpay_order_id:   razorpayOrderId,
            razorpay_signature:  signature,
            items: cartItems.map(item => ({
                product_id:      item.id,
                seller_id: item.seller_id,
                quantity:        item.quantity,
                price:           item.price,
                selected_weight: item.selected_weight || null,
            })),
            address: {
                name:         formData.name,
                phone:        formData.phone,
                email:        formData.email,
                doorNo:       formData.doorNo,
                street:       formData.street,
                area:         formData.area,
                city:         formData.city,
                state:        formData.state,
                instructions: formData.instructions,
            },
            total_amount: estimatedTotal,
        };

        try {
            const res    = await fetch(`${BASE_URL}/verify-payment`, {
                method:  'POST',
                headers: {
                    'Content-Type':  'application/json',
                    'Accept':        'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('user_token')}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await res.json();

            if (result.status) {
                // Clear the cart
                localStorage.removeItem('cart');

                // Go to success page
                navigate('/order-success', {
                    state: { orderId: result.order_id, paymentId, total: estimatedTotal },
                });
            } else {
                alert(`Order save failed: ${result.message}`);
                setLoading(false);
            }

        } catch (err) {
            console.error('Order save failed:', err);
            // Payment went through but order save failed — give them the payment ID
            alert(
                `Payment was received but order save failed.\n` +
                `Please contact support with Payment ID: ${paymentId}`
            );
            setLoading(false);
        }
    };

    return (
        <div className="super-checkout-container">
            <div className="checkout-header">
                <div className="brand-mini">ATR AGRO MART</div>
                <div className="checkout-steps-nav">
                    <span className={step >= 1 ? 'active' : ''}>Receiver</span>
                    <div className="line"></div>
                    <span className={step >= 2 ? 'active' : ''}>Address</span>
                    <div className="line"></div>
                    <span className={step >= 3 ? 'active' : ''}>Payment</span>
                </div>
            </div>

            <div className="checkout-main-content">
                <div className="form-section">

                    {/* ── Step 1: Receiver details ── */}
                    {step === 1 && (
                        <div className="animate-in">
                            <h2 className="section-title">Who is receiving this order?</h2>
                            <div className="input-group">
                                <label>Full Name</label>
                                <input type="text" name="name" value={formData.name}
                                    onChange={handleInputChange} placeholder="e.g. Rajkumar" />
                            </div>
                            <div className="input-row">
                                <div className="input-group">
                                    <label>Phone Number</label>
                                    <input type="number" name="phone" value={formData.phone}
                                        onChange={handleInputChange} placeholder="10-digit mobile number" />
                                </div>
                                <div className="input-group">
                                    <label>Email (Optional)</label>
                                    <input type="email" name="email" value={formData.email}
                                        onChange={handleInputChange} placeholder="for digital invoice" />
                                </div>
                            </div>
                            <button className="primary-btn" onClick={nextStep}>
                                Continue to Address
                            </button>
                        </div>
                    )}

                    {/* ── Step 2: Delivery address ── */}
                    {step === 2 && (
                        <div className="animate-in">
                            <h2 className="section-title">Delivery Address</h2>
                            <div className="address-box-grid">
                                <input type="text" name="doorNo"  value={formData.doorNo}
                                    placeholder="House/Flat No."           onChange={handleInputChange} />
                                <input type="text" name="street"  value={formData.street}
                                    placeholder="Street Name / Colony"     onChange={handleInputChange} />
                                <input type="text" name="area"    value={formData.area}
                                    placeholder="Landmark (e.g. Near Temple)" onChange={handleInputChange} />
                                <input type="text" name="city"    value={formData.city} readOnly />
                            </div>
                            <div className="input-group" style={{ marginTop: 12 }}>
                                <label>Delivery Instructions (optional)</label>
                                <input type="text" name="instructions" value={formData.instructions}
                                    placeholder="e.g. Call before delivery"  onChange={handleInputChange} />
                            </div>
                            <div className="action-row">
                                <button className="text-btn" onClick={prevStep}>Back</button>
                                <button className="primary-btn" onClick={nextStep}>Proceed to Payment</button>
                            </div>
                        </div>
                    )}

                    {/* ── Step 3: Payment ── */}
                    {step === 3 && (
                        <div className="animate-in">
                            <h2 className="section-title">Choose Payment Method</h2>
                            <div className="payment-grid">
                                <label className={`pay-option ${formData.paymentMethod === 'razorpay' ? 'selected' : ''}`}>
                                    <input type="radio" name="paymentMethod" value="razorpay"
                                        checked={formData.paymentMethod === 'razorpay'}
                                        onChange={handleInputChange} />
                                    <div className="pay-info">
                                        <strong>Online Payment</strong>
                                        <span>UPI (GPay), Cards, Netbanking</span>
                                    </div>
                                </label>
                                <label className={`pay-option ${formData.paymentMethod === 'cod' ? 'selected' : ''}`}>
                                    <input type="radio" name="paymentMethod" value="cod"
                                        checked={formData.paymentMethod === 'cod'}
                                        onChange={handleInputChange} />
                                    <div className="pay-info">
                                        <strong>Cash on Delivery</strong>
                                        <span>Pay when you receive the seeds</span>
                                    </div>
                                </label>
                            </div>
                            <button
                                className="primary-btn pay-btn"
                                onClick={handlePlaceOrder}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : `Place Order (₹${estimatedTotal.toFixed(2)})`}
                            </button>
                            <button className="text-btn" onClick={prevStep} disabled={loading}>
                                Go Back to Address
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Order summary sidebar ── */}
                <div className="summary-sidebar">
                    <div className="summary-card">
                        <h3>Order Summary</h3>
                        <div className="item-preview-list">
                            {cartItems.map(item => (
                                
                                <div key={item.id} className="mini-item">
                                    <span>{item.name} (x{item.quantity})</span>
                                    <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="divider"></div>
                        <div className="price-row"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
                        <div className="price-row"><span>GST (0%)</span><span>₹{taxAmount.toFixed(2)}</span></div>
                        <div className="price-row"><span>Delivery Fee</span><span className="free">₹0.00</span></div>
                        <div className="divider"></div>
                        <div className="price-row total">
                            <span>Amount Payable</span>
                            <span>₹{Number(estimatedTotal).toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;